import { yupResolver } from '@hookform/resolvers/yup'

import { Visibility, VisibilityOff } from '@mui/icons-material'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import CryptoJS from 'crypto-js'
import { useContext, useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useLocation, useNavigate } from 'react-router-dom'
import { ModalConfirmDialogMobile } from '../components/ModalConfirmDialogMobile'
import { loadStatus } from '../constants/loadStatus'
import { loginErrorIntervalMinutes, loginErrorLimitTimes } from '../constants/loginLimit'
import { errorMsg } from '../constants/messages'
import { useAuth } from '../hooks/useAuth'
import { useC806AnonymousLogin } from '../hooks/useC806AnonymousLogin'
import { ReactComponent as CotopatImage } from '../images/cotopat-logo.svg'
import type { AnonymousLoginForm } from '../interfaces/AnonymousLoginForm'
import { anonymousLoginSchema } from '../schema/anonymousloginSchema'
import { LanguageContext } from '../util/LanguageContext'

// 匿名ログイン画面
export const AnonymousLogin = () => {
  // hooks
  const auth = useAuth()
  const c806 = useC806AnonymousLogin()

  // 文字列変換用
  const translation = useContext(LanguageContext)

  // 認証loading state
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isDisabled, setIsDisabled] = useState(true)
  // ダイアログ
  const [openDialog, setOpenDialog] = useState(false)
  const [errorDialog, setErrorDialog] = useState('')

  // クエリパラメータ取得
  const search = useLocation().search
  const query = new URLSearchParams(search)
  const convId = query.get('convId')
  // console.log(`convId:${convId}`);
  const navigate = useNavigate()

  // スマホ用vh
  const [vh, setVh] = useState(window.innerHeight * 0.01)

  useEffect(() => {
    const handleResize = () => setVh(window.innerHeight * 0.01)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  if (convId === null) {
    // 有効期限切れ画面に遷移
    navigate('/expired')
  }
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<AnonymousLoginForm>({
    resolver: yupResolver(anonymousLoginSchema),
  })

  const handlePasswordChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value)
    setIsDisabled(event.target.value === '')
    // setError("password", {});
  }

  const handleTogglePasswordVisibility = () => {
    setShowPassword(prev => !prev)
  }

  const onClickLogin = async () => {
    // ログイン失敗回数変数
    const loginIdErrCntName = convId! + 'ErrCnt'
    // ログイン失敗時刻変数
    const loginIdErrTimeName = convId! + 'ErrTime'
    // ローカルストレージからログイン失敗回数とログイン失敗時刻を取得する
    const loginIdErrCnt = localStorage.getItem(loginIdErrCntName)
    const loginIdErrTime = localStorage.getItem(loginIdErrTimeName)
    let errorCount = 0
    const currentTime: number = Date.now()
    if (loginIdErrCnt) {
      errorCount = Number.parseInt(loginIdErrCnt, 10)
    }
    if (loginIdErrTime) {
      const savedTime: number = Number.parseInt(loginIdErrTime, 10)
      const thresholdTime: number = savedTime + loginErrorIntervalMinutes * 60 * 1000
      // console.log(`currentTime:${currentTime} thresholdTime:${thresholdTime} `);
      if (currentTime > thresholdTime) {
        // console.log("一定時間経過している場合ログイン失敗回数を0クリア");
        errorCount = 0
      }
    }
    // ログイン失敗回数を超過している場合
    if (errorCount >= loginErrorLimitTimes) {
      console.log(`ログイン失敗回数超過：${errorCount}`)
      setErrorDialog(errorMsg.QR_AUTH_LIMIT_ERROR)
      setOpenDialog(true)
      setIsDisabled(false)
      return
    }

    // C806会話記録ログイン
    const result = await c806.execAnonymousLogin(convId!, password)
    if (result.success) {
      // ログイン成功時は、ログイン制限チェック関数があれば削除する
      if (loginIdErrCnt) {
        localStorage.removeItem(loginIdErrCntName)
      }
      if (loginIdErrTime) {
        localStorage.removeItem(loginIdErrTimeName)
      }
      // ローカルストレージに保存
      localStorage.setItem('convId', convId!)
      const encrypted = CryptoJS.AES.encrypt(password, 'Cotopat').toString()
      localStorage.setItem('convPassword', encrypted)
      const result = await auth.signInAnonymous(convId!)
      if (result.success) {
        // navigate("/anonymous/conversation");
        navigate(`/index.html?conv=${convId}`)
      } else {
        // 有効期限切れ画面に遷移
        navigate('/expired')
      }
    } else {
      // ログイン失敗ルート
      errorCount++
      // ログイン失敗回数と失敗時刻を書込み
      localStorage.setItem(loginIdErrCntName, errorCount.toString())
      localStorage.setItem(loginIdErrTimeName, currentTime.toString())

      setErrorDialog(result.message!)
      setOpenDialog(true)
    }
  }
  // ダイアログクローズ
  const handleDialogClose = () => {
    setErrorDialog('')
    setOpenDialog(false)
  }

  //多言語化対応
  const userLanguage = navigator.language || navigator.languages[0]
  const baseLanguage = userLanguage.split('-')[0]
  let anonymousLoginButtonJsx: JSX.Element
  switch (baseLanguage) {
    case 'es':
      anonymousLoginButtonJsx = (
        <Button
          type="submit"
          sx={{
            width: '84px',
            height: '37px',
            borderRadius: '6px',
            boxShadow: 0,
            textAlign: 'center',
            font: 'normal normal bold 16px/23px Noto Sans JP',
            letterSpacing: '0px',
            color: isDisabled ? '#F2F2F2' : '#FFFFFF',
            backgroundColor: isDisabled ? '#C2C2C2' : '#282828',
            '&:hover': {
              background: '#282828 0% 0% no-repeat padding-box',
              opacity: 0.8,
              boxShadow: 0,
            },
          }}
          disabled={isDisabled}
        >
          {translation['button_decide']}
        </Button>
      )
      break
    default:
      anonymousLoginButtonJsx = (
        <Button
          type="submit"
          sx={{
            width: '64px',
            height: '37px',
            borderRadius: '6px',
            boxShadow: 0,
            textAlign: 'center',
            font: 'normal normal bold 16px/23px Noto Sans JP',
            letterSpacing: '0px',
            color: isDisabled ? '#F2F2F2' : '#FFFFFF',
            backgroundColor: isDisabled ? '#C2C2C2' : '#282828',
            '&:hover': {
              background: '#282828 0% 0% no-repeat padding-box',
              opacity: 0.8,
              boxShadow: 0,
            },
          }}
          disabled={isDisabled}
        >
          {translation['button_decide']}
        </Button>
      )
  }

  return (
    <form onSubmit={handleSubmit(onClickLogin)}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'auto',
          background: '#F2F2F2 0% 0% no-repeat padding-box',
        }}
      >
        {openDialog && (
          <ModalConfirmDialogMobile
            open={openDialog}
            text={errorDialog}
            onClick={handleDialogClose}
          />
        )}
        <Stack direction="column">
          <Box mt="104px" display="flex" justifyContent="center">
            <CotopatImage width="156px" height="40px" />
          </Box>
          <Typography
            sx={{
              mt: '81px',
              top: '315px',
              left: '759px',
              font: 'normal normal normal 17px/25px Noto Sans JP',
            }}
          >
            {translation['label_password']}
          </Typography>
          <TextField
            type={showPassword ? 'text' : 'password'}
            // value={password}
            {...register('password')}
            error={'password' in errors}
            helperText={errors.password?.message ? translation[errors.password?.message] : ' '}
            onChange={handlePasswordChange}
            InputProps={{
              endAdornment: password ? (
                <InputAdornment position="end">
                  <IconButton onClick={handleTogglePasswordVisibility}>
                    {showPassword ? <Visibility /> : <VisibilityOff />}
                  </IconButton>
                </InputAdornment>
              ) : null,
              sx: {
                background: 'white',
              },
            }}
            size={'small'}
            sx={{
              mt: '5px',
              width: '208px',
              // background: "white",
              borderRadius: '10px',
              'input::-ms-reveal': {
                display: 'none' /* -ms-revealアイコンを非表示にする */,
              },
            }}
            variant="outlined"
          />
        </Stack>
        <Box
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            zIndex: 9999,
          }}
        >
          {(c806.loading === loadStatus.LOADING || auth.loading === loadStatus.LOADING) && (
            <CircularProgress />
          )}
        </Box>
        <Box mt={'70px'}>{anonymousLoginButtonJsx}</Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: 'auto',
          position: 'relative',
          top: `calc(${vh * 100}px - 318.9px - 107px - 24px)`,
          font: 'normal normal normal 8px/8px Noto Sans JP',
        }}
      >
        <div>{translation['copyright_info']}</div>
      </Box>
    </form>
  )
}
